import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../services/api';

const getErrorMessage = (error, fallbackMessage) => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallbackMessage
);

const upsertById = (collection, candidate) => {
  if (!candidate?.id) return collection;

  const targetId = String(candidate.id);
  let found = false;
  const next = (collection || []).map((item) => {
    if (String(item?.id) === targetId) {
      found = true;
      return { ...item, ...candidate };
    }
    return item;
  });

  if (!found) next.unshift(candidate);
  return next;
};

export const estimateQuoteRequest = createAsyncThunk(
  'quotes/estimate',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/quote_requests/estimate', payload);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to estimate quote request'));
    }
  }
);

export const createQuoteRequest = createAsyncThunk(
  'quotes/create',
  async (quoteRequest, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/quote_requests', { quote_request: quoteRequest });
      return data.quote_request || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create quote request'));
    }
  }
);

export const fetchQuoteRequests = createAsyncThunk(
  'quotes/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page);
      if (params.per_page) searchParams.set('per_page', params.per_page);
      const query = searchParams.toString();
      const endpoint = query ? `/quote_requests?${query}` : '/quote_requests';
      const { data } = await api.get(endpoint);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch quote requests'));
    }
  }
);

export const fetchQuoteRequestDetail = createAsyncThunk(
  'quotes/fetchDetail',
  async (quoteRequestId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/quote_requests/${quoteRequestId}`);
      return data.quote_request || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch quote request'));
    }
  }
);

export const cancelQuoteRequest = createAsyncThunk(
  'quotes/cancel',
  async (quoteRequestId, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/quote_requests/${quoteRequestId}/cancel`);
      return data.quote_request || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to cancel quote request'));
    }
  }
);

export const selectSupplierQuote = createAsyncThunk(
  'quotes/selectSupplierQuote',
  async ({ quoteRequestId, supplierQuoteId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/quote_requests/${quoteRequestId}/select_quote`, {
        supplier_quote_id: supplierQuoteId,
      });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to select supplier quote'));
    }
  }
);

const initialState = {
  items: [],
  current: null,
  estimate: null,
  createdOrder: null,
  meta: null,
  loading: false,
  estimating: false,
  error: null,
};

const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    clearQuoteError: (state) => {
      state.error = null;
    },
    clearCurrentQuoteRequest: (state) => {
      state.current = null;
      state.estimate = null;
      state.createdOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(estimateQuoteRequest.pending, (state) => {
        state.estimating = true;
        state.error = null;
      })
      .addCase(estimateQuoteRequest.fulfilled, (state, action) => {
        state.estimating = false;
        state.estimate = action.payload;
      })
      .addCase(estimateQuoteRequest.rejected, (state, action) => {
        state.estimating = false;
        state.error = action.payload;
      })
      .addCase(createQuoteRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuoteRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        state.items = upsertById(state.items, action.payload);
      })
      .addCase(createQuoteRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchQuoteRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload?.quote_requests || [];
        state.meta = action.payload?.meta || null;
      })
      .addCase(fetchQuoteRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchQuoteRequestDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteRequestDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        state.items = upsertById(state.items, action.payload);
      })
      .addCase(fetchQuoteRequestDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cancelQuoteRequest.fulfilled, (state, action) => {
        state.current = action.payload;
        state.items = upsertById(state.items, action.payload);
      })
      .addCase(selectSupplierQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(selectSupplierQuote.fulfilled, (state, action) => {
        state.loading = false;
        const quoteRequest = action.payload?.quote_request;
        state.createdOrder = action.payload?.order || null;
        if (quoteRequest) {
          state.current = quoteRequest;
          state.items = upsertById(state.items, quoteRequest);
        }
      })
      .addCase(selectSupplierQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearQuoteError, clearCurrentQuoteRequest } = quotesSlice.actions;

export default quotesSlice.reducer;
